import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface LicaoAprendida {
  id: string;
  formulario_plano_acao_id: string;
  tipo: 'sucesso' | 'melhoria' | 'falha';
  titulo: string;
  descricao?: string | null;
  impacto?: string | null;
  recomendacoes?: string | null;
  aplicavel_a?: string[] | null;
  registrado_por?: string | null;
  registrado_em?: string | null;
  criado_em: string;
  atualizado_em: string;
  registrador?: { id: string; name: string } | null;
}

interface LicaoAprendidaInsert {
  id?: string;
  formulario_plano_acao_id: string;
  tipo: 'sucesso' | 'melhoria' | 'falha';
  titulo: string;
  descricao?: string | null;
  impacto?: string | null;
  recomendacoes?: string | null;
  aplicavel_a?: string[] | null;
  registrado_por?: string | null;
}

export function useLicoesAprendidasFormulario(formularioPlanoAcaoId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['licoes-aprendidas-formulario', formularioPlanoAcaoId],
    queryFn: async () => {
      if (!formularioPlanoAcaoId) return [];
      const { data, error } = await supabase
        .from('licoes_aprendidas_formulario')
        .select(`
          *,
          registrador:users!licoes_aprendidas_formulario_registrado_por_fkey(id, name)
        `)
        .eq('formulario_plano_acao_id', formularioPlanoAcaoId)
        .order('criado_em', { ascending: false });
      if (error) throw error;
      return data as LicaoAprendida[];
    },
    enabled: !!formularioPlanoAcaoId
  });

  const createMutation = useMutation({
    mutationFn: async (data: LicaoAprendidaInsert) => {
      const payload = {
        ...data,
        registrado_em: new Date().toISOString()
      };

      const { data: result, error } = await supabase
        .from('licoes_aprendidas_formulario')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licoes-aprendidas-formulario', formularioPlanoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['formulario-planos-acao'] });
      toast({ title: 'Lição aprendida registrada com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao registrar lição', description: error.message, variant: 'destructive' });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<LicaoAprendidaInsert> & { id: string }) => {
      const { data: result, error } = await supabase
        .from('licoes_aprendidas_formulario')
        .update({ ...data, atualizado_em: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licoes-aprendidas-formulario', formularioPlanoAcaoId] });
      toast({ title: 'Lição atualizada com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao atualizar lição', description: error.message, variant: 'destructive' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('licoes_aprendidas_formulario')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licoes-aprendidas-formulario', formularioPlanoAcaoId] });
      toast({ title: 'Lição excluída' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao excluir lição', description: error.message, variant: 'destructive' });
    }
  });

  return {
    licoes: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createLicao: createMutation.mutate,
    updateLicao: updateMutation.mutate,
    deleteLicao: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
}
